import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { LoanDetailsCard } from '../fixFlip/loanDetailsCard';
import { Paper, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ItemDisplay } from '../fixFlip/itemDisplay';
import { LoanDetailsAccordion } from '../fixFlip/loanDetailsAccordion';
import { DetailsTable, DetailsTableField } from '../common/detailsTable';
import { getValueOrDefaultDash } from '../../utils/utils';
import { amortizationOptions } from '@roc/feature-loans';
import { LoanSubType } from '../../utils/constants';

interface LoanDetailsCardProps {
  store: any;
}

const useStyles = makeStyles(theme => ({
  paper: {
    border: `1px solid lightgray`,
    borderRadius: '16px !important',
    padding: theme.spacing(4),
  },
}));

export const SinglePropertyLoanDetailsCard = observer(
  ({ store }: LoanDetailsCardProps & { showAccordion: boolean }) => {
    const color = '#34A853';
    const classes = useStyles();
    const loanEconomics = store.loanEconomicsStore.getLoanEconomics();
    const calculatedFields = store.loanEconomicsStore.calculateFields();

    const ltcPurchasePricePercentage = formatPercentage(
      calculatedFields.loanToCost
    );
    const ltcConstructionBudgetHoldbackPercentage = formatPercentage(
      calculatedFields.constructionReserve
    );
    const ARLTV = formatPercentage(calculatedFields.afterRepairLTV);

    const loanPurpose = store.propertiesStore.propertyFormStores[0].form.fields.loanPurpose.value;

    return (
      <Paper className={classes.paper} elevation={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              style={{ paddingInline: '240px' }}
            >
              <Grid item>
                <ItemDisplay
                  label="Total Loan Amount"
                  value={formatCurrency(calculatedFields.totalLoanAmount)}
                  valueTextColor={color}
                />
              </Grid>
              <Grid item>
                <ItemDisplay
                  label="Loan To Value"
                  value={formatPercentage(loanEconomics?.leverage?.loanToValue)}
                  valueTextColor={color}
                />
              </Grid>
              <Grid item>
                <ItemDisplay
                  label="Quote Type"
                  value={loanPurpose}
                  valueTextColor={color}
                />
              </Grid>
              <Grid item>
                <ItemDisplay
                  label="Rate Type"
                  value={loanEconomics?.rates?.rateType}
                  valueTextColor={color}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <DetailsTable fields={getLoanDetails(store)} />
          </Grid>
        </Grid>
      </Paper>
    );
  }
);

function getLoanDetails(store): DetailsTableField[] {
  const values = store.loanEconomicsStore.loanEconomicsFormStore.getFormValues();
  const calculatedFields = store.loanEconomicsStore.calculateFields();
  const amortizationType = amortizationOptions.find(
    opt => opt.value == values.amortizationType
  )?.label;
  return [
    {
      label: 'Amortization Type',
      value: getValueOrDefaultDash(amortizationType),
      colSpan: 1,
    },
    {
      label: 'Prepayment Penalty',
      value: getValueOrDefaultDash(values.prePaymentPenalty),
      colSpan: 1,
    },
    {
      label: 'Pitia DSCR',
      value: getValueOrDefaultDash((calculatedFields.pitiaDSCR ?? 0).toFixed(4)),
      colSpan: 1,
    },
    {
      label: `Today's Rate`,
      value: getValueOrDefaultDash(formatPercentage(values.interestRate)),
      colSpan: 1,
    },
    {
      label: 'Monthly PI Payment',
      value: getValueOrDefaultDash(
        formatCurrency(calculatedFields.monthlyPIPayment)
      ),
      colSpan: 1,
    },
    {
      label: 'Monthly PITIA Payment',
      value: getValueOrDefaultDash(
        formatCurrency(calculatedFields.monthlyPITIAPayment)
      ),
      colSpan: 1,
    },
  ];
}
